import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaShoppingCart,
  FaSearch,
  FaFire,
  FaStar,
  FaTruck,
  FaShieldAlt,
  FaCreditCard,
  FaUndo,
  FaMedkit,
  FaStethoscope,
  FaPills,
  FaPlus,
  FaMinus,
  FaEye,
  FaTimes,
  FaCheckCircle,
  FaExclamationTriangle,
  FaBars,
  FaChevronLeft,
  FaHeart,
  FaRegHeart,
  FaBolt,
  FaTag,
  FaShoppingBag,
  FaArrowRight,
  FaSpinner,
  FaTimesCircle,
  FaUpload,
  FaQrcode,
  FaMapMarkerAlt,
  FaPhone,
  FaUser,
  FaCheck,
} from "react-icons/fa";
import {
  GiMedicines,
  GiHealthPotion,
  GiHeartBeats,
  GiChemicalDrop,
} from "react-icons/gi";
import { motion, AnimatePresence } from "framer-motion";

// Firebase imports
import { database, storage } from '../firebase';
import { ref as dbRef, onValue, push, set, get } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';

const Product = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [cart, setCart] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [wishlist, setWishlist] = useState([]);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Payment checkout states
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentStep, setPaymentStep] = useState(1); // 1: Delivery, 2: Payment, 3: Confirmation
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("");
  const [paymentScreenshot, setPaymentScreenshot] = useState(null);
  const [paymentScreenshotPreview, setPaymentScreenshotPreview] = useState("");
  const [upiId, setUpiId] = useState("");
  const [isProcessingOrder, setIsProcessingOrder] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderId, setOrderId] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [paymentQRCodes, setPaymentQRCodes] = useState({ googlePay: '', phonePe: '' });
  const [qrLoading, setQrLoading] = useState(true);



  // Trust features with animations
  const trustFeatures = [
    {
      id: 1,
      title: "100% Authentic",
      description: "Verified manufacturers only",
      icon: <FaShieldAlt />,
      color: "from-green-500 to-emerald-500",
      delay: 0.1,
    },
    {
      id: 2,
      title: "Secure Payment",
      description: "Encrypted transactions",
      icon: <FaCreditCard />,
      color: "from-blue-500 to-cyan-500",
      delay: 0.2,
    },
    {
      id: 3,
      title: "Fast Delivery",
      description: "2-4 hours in metro cities",
      icon: <FaTruck />,
      color: "from-orange-500 to-yellow-500",
      delay: 0.3,
    },
    {
      id: 4,
      title: "Easy Returns",
      description: "30-day return policy",
      icon: <FaUndo />,
      color: "from-purple-500 to-pink-500",
      delay: 0.4,
    },
  ];

  // Sync cart with Firebase
  useEffect(() => {
    const userId = localStorage.getItem('userId');
    const userMobile = localStorage.getItem('userMobile');

    if (userId && userMobile) {
      const loadCartFromFirebase = async () => {
        try {
          const cartRef = dbRef(database, `Dr/users/${userMobile}/cart`);
          const snapshot = await get(cartRef);

          if (snapshot.exists()) {
            const cartData = snapshot.val();
            const cartArray = Object.keys(cartData).map(key => ({
              ...cartData[key],
              firebaseId: key
            }));
            setCart(cartArray);
          } else {
            setCart([]);
          }
        } catch (error) {
          console.error('Error loading cart from Firebase:', error);
          // Fallback to localStorage
          const cartData = JSON.parse(localStorage.getItem(`cart_${userId}`) || '[]');
          setCart(cartData);
        }
      };

      loadCartFromFirebase();
    } else {
      const guestCart = JSON.parse(localStorage.getItem('guest_cart') || '[]');
      setCart(guestCart);
    }
  }, []);

  // Fetch products from Firebase
  useEffect(() => {
    const productsRef = dbRef(database, 'Dr/products');

    const unsubscribe = onValue(productsRef, (snapshot) => {
      try {
        if (snapshot.exists()) {
          const data = snapshot.val();
          const productsArray = [];

          // Convert Firebase object to array
          Object.keys(data).forEach((key) => {
            if (data[key]) {
              const product = data[key];

              // Determine category based on product name or default to medicines
              const categoryFromName = determineCategory(product.productName);

              // Transform Firebase data to match UI format
              const transformedProduct = {
                id: parseInt(product.productId) || parseInt(key),
                name: product.productName || 'Unnamed Product',
                category: categoryFromName,
                description: product.description || 'No description available',
                longDescription: product.description || 'No detailed description available',
                uses: extractUses(product.description),
                dosage: 'As directed by physician',
                price: product.price || 0,
                discountPrice: product.finalPrice || product.price || 0,
                discountPercent: product.discountType === 'percentage' ? product.discountValue :
                  Math.round(((product.price - product.finalPrice) / product.price) * 100) || 0,
                rating: 4.5, // You can add rating system later
                reviews: 0, // You can add review system later
                inStock: product.status === 'In Stock' || product.stockQuantity > 0,
                isBestSeller: product.stockQuantity > 100, // Products with high stock are best sellers
                stockCount: product.stockQuantity || 0,
                image: product.imageUrl || 'https://images.unsplash.com/photo-1587854692152-cbe660dbde88?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
                icon: getCategoryIcon(categoryFromName),
                color: getCategoryColor(categoryFromName),
                bgColor: getCategoryBgColor(categoryFromName),
                features: extractFeatures(product.description),
                manufacturer: product.manufacturer || 'Not specified'
              };

              productsArray.push(transformedProduct);
            }
          });

          setProducts(productsArray);
          setLoading(false);
        } else {
          setProducts([]);
          setLoading(false);
        }
      } catch (err) {
        console.error('Error fetching products:', err);
        setError('Failed to load products');
        setLoading(false);
      }
    }, (error) => {
      console.error('Firebase error:', error);
      setError('Failed to connect to database');
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Fetch payment QR codes from Firebase
  useEffect(() => {
    const qrRef = dbRef(database, 'Dr/paymentQR');

    const unsubscribe = onValue(qrRef, (snapshot) => {
      try {
        if (snapshot.exists()) {
          const data = snapshot.val();
          setPaymentQRCodes({
            googlePay: data.googlePay || '',
            phonePe: data.phonePe || ''
          });
        }
        setQrLoading(false);
      } catch (err) {
        console.error('Error fetching payment QR codes:', err);
        setQrLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // Helper function to determine category from product name
  const determineCategory = (productName) => {
    if (!productName) return 'medicines';
    const name = productName.toLowerCase();

    if (name.includes('vitamin') || name.includes('supplement') || name.includes('omega') ||
      name.includes('protein') || name.includes('calcium')) {
      return 'supplements';
    } else if (name.includes('thermometer') || name.includes('monitor') || name.includes('meter') ||
      name.includes('strips') || name.includes('glucometer') || name.includes('bp')) {
      return 'equipment';
    } else {
      return 'medicines';
    }
  };

  // Helper function to get category icon
  const getCategoryIcon = (category) => {
    switch (category) {
      case 'medicines':
        return <GiMedicines className="text-4xl" />;
      case 'equipment':
        return <FaStethoscope className="text-4xl" />;
      case 'supplements':
        return <GiHealthPotion className="text-4xl" />;
      default:
        return <FaMedkit className="text-4xl" />;
    }
  };

  // Helper function to get category color
  const getCategoryColor = (category) => {
    switch (category) {
      case 'medicines':
        return 'from-blue-500 to-cyan-500';
      case 'equipment':
        return 'from-green-500 to-emerald-500';
      case 'supplements':
        return 'from-purple-500 to-pink-500';
      default:
        return 'from-teal-500 to-emerald-500';
    }
  };

  // Helper function to get category background color
  const getCategoryBgColor = (category) => {
    switch (category) {
      case 'medicines':
        return 'bg-gradient-to-r from-blue-50 to-cyan-50';
      case 'equipment':
        return 'bg-gradient-to-r from-green-50 to-emerald-50';
      case 'supplements':
        return 'bg-gradient-to-r from-purple-50 to-pink-50';
      default:
        return 'bg-gradient-to-r from-teal-50 to-emerald-50';
    }
  };

  // Helper function to extract uses from description
  const extractUses = (description) => {
    if (!description) return ['General health'];
    // Simple extraction - you can enhance this
    return [description.substring(0, 50) + '...'];
  };

  // Helper function to extract features from description
  const extractFeatures = (description) => {
    if (!description) return ['Quality Product', 'Trusted Brand', 'Fast Acting'];
    return ['Quality Product', 'Trusted Brand', 'Doctor Recommended'];
  };

  // Filter products based on search only
  const filteredProducts = products.filter((product) => {
    if (
      searchQuery &&
      !product.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
      !product.description.toLowerCase().includes(searchQuery.toLowerCase())
    )
      return false;
    return true;
  });

  const handleAddToCart = (product) => {
    // Check if user is logged in
    const userId = localStorage.getItem('userId');
    const userName = localStorage.getItem('userName');
    const userMobile = localStorage.getItem('userMobile');

    // Check if user is logged in
    const isLoggedIn = userId && userMobile;

    if (isLoggedIn) {
      // Add to Firebase for logged-in user
      const addToFirebase = async () => {
        try {
          const cartRef = dbRef(database, `Dr/users/${userMobile}/cart`);
          const snapshot = await get(cartRef);

          let cartData = {};
          if (snapshot.exists()) {
            cartData = snapshot.val();
          }

          // Check if product already exists in cart
          let existingItemKey = null;
          let existingItem = null;

          Object.keys(cartData).forEach(key => {
            if (cartData[key].id === product.id) {
              existingItemKey = key;
              existingItem = cartData[key];
            }
          });

          if (existingItem) {
            // Update quantity if product already exists
            cartData[existingItemKey] = {
              ...existingItem,
              quantity: existingItem.quantity + 1,
              updatedAt: new Date().toISOString()
            };
          } else {
            // Add new product to cart
            const newItemKey = `item_${product.id}_${Date.now()}`;
            cartData[newItemKey] = {
              id: product.id,
              name: product.name,
              price: product.discountPrice,
              quantity: 1,
              image: product.image,
              category: product.category,
              description: product.description,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            };
          }

          // Save to Firebase
          await set(cartRef, cartData);

          // Update local state for immediate UI feedback
          const cartArray = Object.keys(cartData).map(key => ({
            ...cartData[key],
            firebaseId: key
          }));
          setCart(cartArray);

          // Animation feedback
          const button = document.getElementById(`add-to-cart-${product.id}`);
          if (button) {
            button.classList.add("animate-bounce");
            setTimeout(() => button.classList.remove("animate-bounce"), 500);
          }

          // Show success message
          alert(`${product.name} added to cart successfully!`);

        } catch (error) {
          console.error('Error adding to cart:', error);
          alert('Failed to add to cart. Please try again.');
        }
      };

      addToFirebase();
    } else {
      // Add to localStorage for guest user
      try {
        const guestCart = JSON.parse(localStorage.getItem('guest_cart') || '[]');
        const existingItemIndex = guestCart.findIndex(item => item.id === product.id);

        if (existingItemIndex >= 0) {
          guestCart[existingItemIndex].quantity += 1;
        } else {
          const cartItem = {
            id: product.id,
            name: product.name,
            price: product.discountPrice,
            quantity: 1,
            image: product.image,
            category: product.category,
            description: product.description
          };
          guestCart.push(cartItem);
        }

        localStorage.setItem('guest_cart', JSON.stringify(guestCart));
        setCart(guestCart); // Update local state

        // Animation feedback
        const button = document.getElementById(`add-to-cart-${product.id}`);
        if (button) {
          button.classList.add("animate-bounce");
          setTimeout(() => button.classList.remove("animate-bounce"), 500);
        }

        alert(`${product.name} added to cart successfully!`);
        // Navigate to Cart page as requested by user
        navigate('/cart');

      } catch (error) {
        console.error('Error adding to guest cart:', error);
        alert('Failed to add to cart.');
      }
    }
  };

  const handleBuyNow = (product) => {
    // Get user details from localStorage (if available)
    const userMobile = localStorage.getItem('userMobile');

    console.log('Buy Now clicked - Opening payment modal');

    // Directly open payment modal without login check
    setSelectedProduct(product);
    setQuantity(1);
    setShowPaymentModal(true);
    setPaymentStep(1);

    // Pre-fill phone number if available
    if (userMobile) {
      setPhoneNumber(userMobile);
    }
  };

  const handleViewDetails = (product) => {
    setSelectedProduct(product);
    setQuantity(1);
  };

  const handleBackToList = () => {
    setSelectedProduct(null);
  };

  const handleQuantityChange = (type) => {
    if (type === "increase") {
      setQuantity((prev) => prev + 1);
    } else if (type === "decrease" && quantity > 1) {
      setQuantity((prev) => prev - 1);
    }
  };

  const toggleWishlist = (productId) => {
    setWishlist((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    );
  };

  // Payment handler functions
  const handlePaymentScreenshotUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        setPaymentScreenshot(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setPaymentScreenshotPreview(reader.result);
        };
        reader.readAsDataURL(file);
        setFormErrors(prev => ({ ...prev, screenshot: '' }));
      } else {
        setFormErrors(prev => ({ ...prev, screenshot: 'Please upload a valid image file' }));
      }
    }
  };

  const validateDeliveryDetails = () => {
    const errors = {};

    if (!deliveryAddress.trim() || deliveryAddress.trim().length < 10) {
      errors.address = 'Please enter a complete delivery address (minimum 10 characters)';
    }

    const phoneRegex = /^[6-9]\d{9}$/;
    if (!phoneNumber.trim()) {
      errors.phone = 'Phone number is required';
    } else if (!phoneRegex.test(phoneNumber.trim())) {
      errors.phone = 'Please enter a valid 10-digit Indian mobile number';
    }

    return errors;
  };

  const validatePaymentDetails = () => {
    const errors = {};

    if (!selectedPaymentMethod) {
      errors.paymentMethod = 'Please select a payment method';
    }

    if (!paymentScreenshot) {
      errors.screenshot = 'Please upload payment screenshot';
    }

    const upiRegex = /^[\w.-]+@[\w.-]+$/;
    if (!upiId.trim()) {
      errors.upiId = 'UPI ID is required';
    } else if (!upiRegex.test(upiId.trim())) {
      errors.upiId = 'Please enter a valid UPI ID (e.g., username@paytm)';
    }

    return errors;
  };

  const handleContinueToPayment = () => {
    const errors = validateDeliveryDetails();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    setPaymentStep(2);
  };

  const handleSubmitOrder = async () => {
    const errors = validatePaymentDetails();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsProcessingOrder(true);
    setFormErrors({});

    try {
      // Get user details from localStorage
      const userId = localStorage.getItem('userId');
      const userName = localStorage.getItem('userName');
      const userMobile = localStorage.getItem('userMobile');

      console.log('Starting order submission for mobile:', userMobile);

      // Step 1: Upload payment screenshot to Firebase Storage
      let screenshotURL = '';
      try {
        const timestamp = Date.now();
        const screenshotFileName = `payment_${userMobile}_${timestamp}_${paymentScreenshot.name}`;
        const screenshotStorageRef = storageRef(storage, `Dr/orders/${userMobile}/screenshots/${screenshotFileName}`);

        console.log('Uploading screenshot to:', `Dr/orders/${userMobile}/screenshots/${screenshotFileName}`);

        // Upload the file
        const uploadResult = await uploadBytes(screenshotStorageRef, paymentScreenshot);
        console.log('Screenshot uploaded successfully');

        // Get the download URL
        screenshotURL = await getDownloadURL(uploadResult.ref);
        console.log('Screenshot URL:', screenshotURL);
      } catch (uploadError) {
        console.error('Error uploading screenshot:', uploadError);
        setFormErrors({ submit: 'Failed to upload payment screenshot. Please try again.' });
        setIsProcessingOrder(false);
        return;
      }

      // Step 2: Get the next order number for this mobile number
      const userOrdersRef = dbRef(database, `Dr/orders/${userMobile}`);
      const userOrdersSnapshot = await get(userOrdersRef);

      let nextOrderNumber = 1;
      if (userOrdersSnapshot.exists()) {
        const existingOrders = userOrdersSnapshot.val();
        // Get all numeric keys and find the maximum
        const orderNumbers = Object.keys(existingOrders)
          .map(key => parseInt(key))
          .filter(num => !isNaN(num));

        if (orderNumbers.length > 0) {
          nextOrderNumber = Math.max(...orderNumbers) + 1;
        }
      }

      console.log('Next order number for mobile', userMobile, ':', nextOrderNumber);

      // Step 3: Create order object
      const orderData = {
        orderId: `ORD${Date.now()}`,
        orderNumber: nextOrderNumber,
        userId: userId,
        userName: userName,
        userMobile: userMobile,
        product: {
          id: selectedProduct.id,
          name: selectedProduct.name,
          price: selectedProduct.discountPrice,
          quantity: quantity,
          totalPrice: selectedProduct.discountPrice * quantity,
          image: selectedProduct.image,
          category: selectedProduct.category,
          description: selectedProduct.description
        },
        deliveryDetails: {
          address: deliveryAddress,
          phoneNumber: phoneNumber
        },
        paymentDetails: {
          method: selectedPaymentMethod,
          upiId: upiId,
          screenshotURL: screenshotURL,
          screenshotName: paymentScreenshot.name,
          screenshotSize: paymentScreenshot.size
        },
        orderStatus: 'Pending Verification',
        paymentStatus: 'Pending Verification',
        timestamp: new Date().toISOString(),
        createdAt: Date.now()
      };

      // Step 4: Save order to Firebase under mobile number with sequential number
      const orderRef = dbRef(database, `Dr/orders/${userMobile}/${nextOrderNumber}`);
      await set(orderRef, orderData);

      console.log('Order saved successfully at:', `Dr/orders/${userMobile}/${nextOrderNumber}`);

      setOrderId(orderData.orderId);
      setOrderSuccess(true);
      setPaymentStep(3);
      setIsProcessingOrder(false);

      // Reset form after 5 seconds
      setTimeout(() => {
        handleClosePaymentModal();
      }, 5000);

    } catch (error) {
      console.error('Error submitting order:', error);
      setFormErrors({ submit: 'Failed to submit order. Please try again.' });
      setIsProcessingOrder(false);
    }
  };

  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
    setPaymentStep(1);
    setDeliveryAddress('');
    setPhoneNumber('');
    setSelectedPaymentMethod('');
    setPaymentScreenshot(null);
    setPaymentScreenshotPreview('');
    setUpiId('');
    setFormErrors({});
    setOrderSuccess(false);
    setOrderId('');
  };

  const handleBackToDelivery = () => {
    setPaymentStep(1);
    setFormErrors({});
  };

  const totalCartItems = cart.reduce((total, item) => total + item.quantity, 0);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
      },
    },
  };

  const cardHoverVariants = {
    hover: {
      y: -5,
      scale: 1.02,
      transition: {
        type: "spring",
        stiffness: 300,
      },
    },
  };

  const fadeInVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.5 } },
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-72 h-72 bg-gradient-to-r from-teal-200/20 to-emerald-200/20 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-r from-blue-200/20 to-purple-200/20 rounded-full blur-3xl translate-x-1/3 translate-y-1/3"></div>
      </div>

      {/* Header */}
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ type: "spring", stiffness: 100 }}
        className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-gray-200 shadow-sm"
      >
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="flex items-center space-x-3"
            >
              <div className="bg-gradient-to-r from-teal-500 to-emerald-500 p-2 rounded-xl">
                <FaMedkit className="text-2xl text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-teal-600 to-emerald-600 bg-clip-text text-transparent">
                  MedShop
                </h1>
                <p className="text-xs text-gray-500">Trusted Healthcare</p>
              </div>
            </motion.div>

            {/* Search Bar */}
            <div className="flex-1 max-w-2xl mx-6 hidden lg:block">
              <motion.div
                animate={{ scale: isSearchFocused ? 1.02 : 1 }}
                className="relative"
              >
                <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search medicines, equipment, supplements..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setIsSearchFocused(true)}
                  onBlur={() => setIsSearchFocused(false)}
                  className="w-full pl-12 pr-4 py-3 bg-gray-100/80 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all"
                />
              </motion.div>
            </div>

            {/* Cart */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="relative p-3 bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl border border-teal-200"
            >
              <FaShoppingCart className="text-teal-600 text-xl" />
              {totalCartItems > 0 && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -top-2 -right-2 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center"
                >
                  {totalCartItems}
                </motion.span>
              )}
            </motion.button>
          </div>

          {/* Mobile Search */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 lg:hidden"
          >
            <div className="relative">
              <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-gray-100 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none"
              />
            </div>
          </motion.div>
        </div>
      </motion.header>

      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 py-6">
        <AnimatePresence mode="wait">
          {!selectedProduct ? (
            /* Product List View */
            <motion.div
              key="list"
              initial="hidden"
              animate="visible"
              variants={containerVariants}
              exit="hidden"
            >
              {/* Hero Section */}
              <motion.div variants={itemVariants} className="mb-8">
                <div className="bg-gradient-to-r from-teal-500 to-emerald-500 rounded-2xl p-8 text-white overflow-hidden relative">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-32 translate-x-32"></div>
                  <div className="relative z-10">
                    <h1 className="text-3xl font-bold mb-3">
                      Your Health, Our Priority
                    </h1>
                    <p className="text-teal-100 mb-6 max-w-2xl">
                      Discover authentic medicines, premium supplements, and
                      reliable health equipment at unbeatable prices
                    </p>
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        <FaBolt className="text-yellow-300" />
                        <span className="font-semibold">Fast Delivery</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <FaShieldAlt className="text-blue-300" />
                        <span className="font-semibold">100% Authentic</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <FaCreditCard className="text-green-300" />
                        <span className="font-semibold">Secure Payment</span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Product Grid */}
              {loading ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center py-16"
                >
                  <FaSpinner className="text-5xl text-teal-500 mx-auto mb-4 animate-spin" />
                  <h3 className="text-xl font-bold text-gray-600 mb-2">
                    Loading Products...
                  </h3>
                  <p className="text-gray-500">
                    Please wait while we fetch the latest products
                  </p>
                </motion.div>
              ) : error ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center py-16 bg-red-50 rounded-2xl border border-red-200 p-8"
                >
                  <FaTimesCircle className="text-5xl text-red-500 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-red-700 mb-2">
                    Error Loading Products
                  </h3>
                  <p className="text-red-600 mb-4">{error}</p>
                  <button
                    onClick={() => window.location.reload()}
                    className="bg-gradient-to-r from-red-500 to-pink-500 text-white px-6 py-3 rounded-lg hover:shadow-lg transition-all"
                  >
                    Retry
                  </button>
                </motion.div>
              ) : filteredProducts.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center py-16"
                >
                  <FaSearch className="text-5xl text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-600 mb-2">
                    No Products Found
                  </h3>
                  <p className="text-gray-500">
                    Try adjusting your search or filters
                  </p>
                </motion.div>
              ) : (
                <motion.div
                  variants={containerVariants}
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
                >
                  {filteredProducts.map((product, index) => (
                    <motion.div
                      key={product.id}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true, amount: 0.2 }}
                      variants={{
                        hidden: { y: 20, opacity: 0 },
                        visible: {
                          y: 0,
                          opacity: 1,
                          transition: {
                            type: "spring",
                            stiffness: 100,
                            delay: index * 0.1, // staggered effect
                          },
                        },
                        hover: {
                          y: -5,
                          scale: 1.02,
                          transition: {
                            type: "spring",
                            stiffness: 300,
                          },
                        },
                      }}
                      whileHover="hover"
                      className="group"
                    >
                      <div className="bg-white rounded-2xl shadow-sm hover:shadow-xl border border-gray-200 overflow-hidden transition-all duration-300">
                        {/* Product Image */}
                        <div className="relative h-56 overflow-hidden">
                          <motion.img
                            src={product.image}
                            alt={product.name}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                            initial={{ scale: 1.1 }}
                            whileInView={{ scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5 }}
                          />

                          {/* Badges */}
                          <div className="absolute top-3 left-3 space-y-2">
                            {product.discountPercent > 0 && (
                              <motion.div
                                initial={{ x: -20, opacity: 0 }}
                                whileInView={{ x: 0, opacity: 1 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.2 }}
                                className="bg-gradient-to-r from-red-500 to-pink-500 text-white px-3 py-1 rounded-lg text-xs font-bold shadow-lg"
                              >
                                {product.discountPercent}% OFF
                              </motion.div>
                            )}
                            {product.isBestSeller && (
                              <motion.div
                                initial={{ x: -20, opacity: 0 }}
                                whileInView={{ x: 0, opacity: 1 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.3 }}
                                className="bg-gradient-to-r from-orange-500 to-yellow-500 text-white px-3 py-1 rounded-lg text-xs font-bold shadow-lg"
                              >
                                <FaFire className="inline mr-1" /> Best Seller
                              </motion.div>
                            )}
                          </div>

                          {/* Wishlist Button */}
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleWishlist(product.id);
                            }}
                            className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm p-2 rounded-full shadow-lg"
                          >
                            {wishlist.includes(product.id) ? (
                              <FaHeart className="text-red-500" />
                            ) : (
                              <FaRegHeart className="text-gray-400" />
                            )}
                          </motion.button>

                          {/* Out of Stock Overlay */}
                          {!product.inStock && (
                            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                              <span className="bg-gray-800 text-white px-4 py-2 rounded-lg font-bold">
                                Out of Stock
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Product Info */}
                        <div className="p-5">
                          {/* Category */}
                          <div className="flex items-center mb-3">
                            <div
                              className={`bg-gradient-to-r ${product.color} p-2 rounded-lg`}
                            >
                              {product.icon}
                            </div>
                            <span className="ml-3 text-sm font-medium text-gray-500">
                              {product.category.charAt(0).toUpperCase() +
                                product.category.slice(1)}
                            </span>
                          </div>

                          {/* Name */}
                          <h3 className="font-bold text-gray-800 mb-2 line-clamp-2 text-lg">
                            {product.name}
                          </h3>

                          {/* Description */}
                          <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                            {product.description}
                          </p>

                          {/* Rating */}
                          <div className="flex items-center mb-4">
                            <div className="flex">
                              {[...Array(5)].map((_, i) => (
                                <FaStar
                                  key={i}
                                  className={`${i < Math.floor(product.rating)
                                    ? "text-yellow-400"
                                    : "text-gray-300"
                                    } mr-1`}
                                  size={14}
                                />
                              ))}
                            </div>
                            <span className="text-gray-600 text-sm ml-2">
                              ({product.reviews})
                            </span>
                          </div>

                          {/* Price */}
                          <div className="flex items-center justify-between mb-4">
                            <div>
                              <div className="flex items-center">
                                <span className="text-xl font-bold text-gray-800">
                                  ₹{product.discountPrice}
                                </span>
                                {product.discountPrice < product.price && (
                                  <span className="text-gray-500 line-through ml-2">
                                    ₹{product.price}
                                  </span>
                                )}
                              </div>
                              {product.inStock ? (
                                <div className="flex items-center text-green-600 text-sm mt-1">
                                  <FaCheckCircle className="mr-2" />
                                  <span>In Stock</span>
                                </div>
                              ) : (
                                <div className="flex items-center text-red-600 text-sm mt-1">
                                  <FaExclamationTriangle className="mr-2" />
                                  <span>Out of Stock</span>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="grid grid-cols-2 gap-3">
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => handleViewDetails(product)}
                              className="flex items-center justify-center space-x-2 bg-gray-100 text-gray-700 px-4 py-2.5 rounded-xl hover:bg-gray-200 transition-colors"
                            >
                              <FaEye />
                              <span className="font-medium">Buy Now</span>
                            </motion.button>

                            <motion.button
                              id={`add-to-cart-${product.id}`}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() =>
                                product.inStock && handleAddToCart(product)
                              }
                              disabled={!product.inStock}
                              className={`flex items-center justify-center space-x-2 px-4 py-2.5 rounded-xl font-medium ${product.inStock
                                ? "bg-gradient-to-r from-teal-500 to-emerald-500 text-white hover:shadow-lg"
                                : "bg-gray-200 text-gray-400 cursor-not-allowed"
                                }`}
                            >
                              <FaShoppingCart />
                              <span>Add to Cart</span>
                            </motion.button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              )}

              {/* Trust Features */}
              <motion.div variants={itemVariants} className="mt-16">
                <div className="text-center mb-10">
                  <h2 className="text-2xl font-bold text-gray-800 mb-3">
                    Why Choose MedShop?
                  </h2>
                  <p className="text-gray-600 max-w-2xl mx-auto">
                    We're committed to providing you with the best healthcare
                    experience
                  </p>
                </div>

                <motion.div
                  variants={containerVariants}
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
                >
                  {trustFeatures.map((feature, index) => (
                    <motion.div
                      key={feature.id}
                      variants={itemVariants}
                      custom={index}
                      whileHover={{ y: -5 }}
                      className="bg-white rounded-2xl p-6 border border-gray-200 hover:border-teal-200 hover:shadow-lg transition-all"
                    >
                      <div
                        className={`bg-gradient-to-r ${feature.color} w-14 h-14 rounded-xl flex items-center justify-center mb-4`}
                      >
                        <div className="text-white text-xl">{feature.icon}</div>
                      </div>
                      <h4 className="font-bold text-gray-800 mb-2 text-lg">
                        {feature.title}
                      </h4>
                      <p className="text-gray-600">{feature.description}</p>
                    </motion.div>
                  ))}
                </motion.div>
              </motion.div>
            </motion.div>
          ) : (
            /* Product Detail View */
            <motion.div
              key="detail"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Back Button */}
              <motion.button
                whileHover={{ x: -5 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleBackToList}
                className="flex items-center text-teal-600 hover:text-teal-700 mb-6 font-medium"
              >
                <FaChevronLeft className="mr-2" />
                Back to Products
              </motion.button>

              {/* Product Detail Card */}
              <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-200">
                <div className="flex flex-col lg:flex-row">
                  {/* Image Section */}
                  <div className="lg:w-2/5 p-6 lg:p-8">
                    <div className="relative h-64 lg:h-96 rounded-2xl overflow-hidden">
                      <motion.img
                        src={selectedProduct.image}
                        alt={selectedProduct.name}
                        className="w-full h-full object-cover"
                        initial={{ scale: 1.1 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.5 }}
                      />

                      {/* Badges */}
                      <div className="absolute top-4 left-4 space-y-2">
                        {selectedProduct.discountPercent > 0 && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="bg-gradient-to-r from-red-500 to-pink-500 text-white px-4 py-2 rounded-lg font-bold shadow-lg"
                          >
                            {selectedProduct.discountPercent}% OFF
                          </motion.div>
                        )}
                        {selectedProduct.isBestSeller && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.1 }}
                            className="bg-gradient-to-r from-orange-500 to-yellow-500 text-white px-4 py-2 rounded-lg font-bold shadow-lg"
                          >
                            <FaFire className="inline mr-2" /> Best Seller
                          </motion.div>
                        )}
                      </div>
                    </div>

                    {/* Stock Status */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                      className="mt-6"
                    >
                      {selectedProduct.inStock ? (
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-2xl border border-green-200">
                          <div className="flex items-center">
                            <FaCheckCircle className="text-green-500 text-2xl mr-3" />
                            <div>
                              <h4 className="font-bold text-green-700">
                                In Stock
                              </h4>
                              <p className="text-green-600 text-sm">
                                {selectedProduct.stockCount} units available •
                                Fast delivery
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="bg-gradient-to-r from-red-50 to-pink-50 p-4 rounded-2xl border border-red-200">
                          <div className="flex items-center">
                            <FaExclamationTriangle className="text-red-500 text-2xl mr-3" />
                            <div>
                              <h4 className="font-bold text-red-700">
                                Out of Stock
                              </h4>
                              <p className="text-red-600 text-sm">
                                Get notified when available
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </motion.div>

                    {/* Features */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                      className="mt-6"
                    >
                      <h4 className="font-bold text-gray-800 mb-3">
                        Key Features
                      </h4>
                      <div className="space-y-2">
                        {selectedProduct.features?.map((feature, index) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.1 * index }}
                            className="flex items-center text-gray-600"
                          >
                            <FaCheckCircle className="text-teal-500 mr-2" />
                            {feature}
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  </div>

                  {/* Details Section */}
                  <div className="lg:w-3/5 p-6 lg:p-8">
                    {/* Header */}
                    <div className="mb-6">
                      <div className="flex items-center mb-4">
                        <div
                          className={`bg-gradient-to-r ${selectedProduct.color} p-3 rounded-xl`}
                        >
                          {selectedProduct.icon}
                        </div>
                        <span className="ml-3 text-sm font-medium text-gray-500 uppercase tracking-wider">
                          {selectedProduct.category}
                        </span>
                      </div>

                      <h1 className="text-3xl lg:text-4xl font-bold text-gray-800 mb-4">
                        {selectedProduct.name}
                      </h1>

                      <div className="flex items-center mb-6">
                        <div className="flex mr-4">
                          {[...Array(5)].map((_, i) => (
                            <FaStar
                              key={i}
                              className={`${i < Math.floor(selectedProduct.rating)
                                ? "text-yellow-400"
                                : "text-gray-300"
                                } mr-1`}
                              size={18}
                            />
                          ))}
                        </div>
                        <span className="text-gray-700 font-bold">
                          {selectedProduct.rating}/5
                        </span>
                        {/* <span className="text-gray-500 ml-2">
                          ({selectedProduct.reviews} reviewss)
                        </span> */}
                      </div>
                    </div>

                    {/* Price */}
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.1 }}
                      className="mb-8"
                    >
                      <div className="flex items-center mb-2">
                        <span className="text-4xl lg:text-5xl font-bold text-gray-800">
                          ₹{selectedProduct.discountPrice}
                        </span>
                        {selectedProduct.discountPrice <
                          selectedProduct.price && (
                            <>
                              <span className="text-2xl text-gray-500 line-through ml-4">
                                ₹{selectedProduct.price}
                              </span>
                              <motion.span
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                className="ml-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-1 rounded-lg font-bold"
                              >
                                Save ₹
                                {selectedProduct.price -
                                  selectedProduct.discountPrice}
                              </motion.span>
                            </>
                          )}
                      </div>
                    </motion.div>

                    {/* Description */}
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.2 }}
                      className="mb-8"
                    >
                      <h3 className="text-xl font-bold text-gray-800 mb-3">
                        Description
                      </h3>
                      <p className="text-gray-700 leading-relaxed">
                        {selectedProduct.longDescription}
                      </p>
                    </motion.div>

                    {/* Uses */}
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.3 }}
                      className="mb-8"
                    >
                      <h3 className="text-xl font-bold text-gray-800 mb-3">
                        Uses
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {selectedProduct.uses.map((use, index) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.05 * index }}
                            className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-xl"
                          >
                            <div className="flex items-center">
                              <FaCheckCircle className="text-teal-500 mr-3" />
                              <span className="text-gray-700">{use}</span>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>

                    {/* Quantity Selector */}
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.4 }}
                      className="mb-8"
                    >
                      <h3 className="text-xl font-bold text-gray-800 mb-3">
                        Quantity
                      </h3>
                      <div className="flex items-center">
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => handleQuantityChange("decrease")}
                          className="bg-gray-100 text-gray-700 w-12 h-12 rounded-l-xl flex items-center justify-center hover:bg-gray-200"
                        >
                          <FaMinus />
                        </motion.button>
                        <div className="bg-white border-y border-gray-200 w-16 h-12 flex items-center justify-center font-bold text-xl">
                          {quantity}
                        </div>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => handleQuantityChange("increase")}
                          className="bg-gray-100 text-gray-700 w-12 h-12 rounded-r-xl flex items-center justify-center hover:bg-gray-200"
                        >
                          <FaPlus />
                        </motion.button>
                        <div className="ml-6">
                          <div className="text-gray-600">Total Price</div>
                          <div className="text-2xl font-bold text-gray-800">
                            ₹{selectedProduct.discountPrice * quantity}
                          </div>
                        </div>
                      </div>
                    </motion.div>

                    {/* Action Buttons */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 }}
                      className="flex flex-col sm:flex-row gap-4"
                    >
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() =>
                          selectedProduct.inStock &&
                          handleAddToCart(selectedProduct)
                        }
                        disabled={!selectedProduct.inStock}
                        className={`flex items-center justify-center space-x-3 px-8 py-4 rounded-xl font-bold text-lg ${selectedProduct.inStock
                          ? "bg-gradient-to-r from-teal-500 to-emerald-500 hover:shadow-xl"
                          : "bg-gray-300 cursor-not-allowed"
                          } text-white shadow-lg transition-all flex-1`}
                      >
                        <FaShoppingCart />
                        <span>Add to Cart</span>
                      </motion.button>

                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() =>
                          selectedProduct.inStock &&
                          handleBuyNow(selectedProduct)
                        }
                        disabled={!selectedProduct.inStock}
                        className={`flex items-center justify-center space-x-3 px-8 py-4 rounded-xl font-bold text-lg ${selectedProduct.inStock
                          ? "bg-gradient-to-r from-blue-500 to-indigo-500 hover:shadow-xl"
                          : "bg-gray-300 cursor-not-allowed"
                          } text-white shadow-lg transition-all flex-1`}
                      >
                        <FaCreditCard />
                        <span>Buy Now</span>
                      </motion.button>
                    </motion.div>

                    {/* Dosage Info */}
                    {/* <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.6 }}
                      className="mt-8 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6"
                     >
                      <h3 className="text-xl font-bold text-gray-800 mb-3">
                        Dosage Information
                      </h3>
                      <p className="text-gray-700 mb-4">
                        {selectedProduct.dosage}
                      </p>
                      <div className="bg-white/80 rounded-xl p-4">
                        <div className="flex items-start">
                          <FaExclamationTriangle className="text-red-500 text-xl mr-3 flex-shrink-0 mt-1" />
                          <p className="text-red-600 text-sm">
                            Important: Consult with a healthcare professional
                            for personalized dosage instructions. Do not exceed
                            recommended dosage. Store in a cool, dry place away
                            from children.
                          </p>
                        </div>
                      </div>
                    </motion.div> */}
                  </div>
                </div>
              </div>

              {/* Related Products */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
                className="mt-12"
              >
                <h3 className="text-2xl font-bold text-gray-800 mb-6">
                  Related Products
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {products
                    .filter(
                      (p) =>
                        p.category === selectedProduct.category &&
                        p.id !== selectedProduct.id
                    )
                    .slice(0, 4)
                    .map((product) => (
                      <motion.div
                        key={product.id}
                        whileHover={{ y: -5 }}
                        onClick={() => handleViewDetails(product)}
                        className="bg-white rounded-xl p-4 shadow-sm hover:shadow-xl border border-gray-200 cursor-pointer transition-all"
                      >
                        <div className="h-40 rounded-lg overflow-hidden mb-3">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <h4 className="font-bold text-gray-800 mb-2 line-clamp-2">
                          {product.name}
                        </h4>
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-gray-800">
                            ₹{product.discountPrice}
                          </span>
                          <button className="text-teal-600 hover:text-teal-700 font-medium flex items-center">
                            View <FaArrowRight className="ml-2" />
                          </button>
                        </div>
                      </motion.div>
                    ))}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Payment Modal */}
      <AnimatePresence>
        {showPaymentModal && selectedProduct && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={handleClosePaymentModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="sticky top-0 bg-gradient-to-r from-teal-500 to-emerald-500 text-white p-6 rounded-t-3xl z-10">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold mb-1">Checkout</h2>
                    <p className="text-teal-100 text-sm">
                      {paymentStep === 1 && "Step 1: Delivery Details"}
                      {paymentStep === 2 && "Step 2: Payment"}
                      {paymentStep === 3 && "Order Confirmed!"}
                    </p>
                  </div>
                  <button
                    onClick={handleClosePaymentModal}
                    className="bg-white/20 hover:bg-white/30 p-2 rounded-full transition-colors"
                  >
                    <FaTimes className="text-xl" />
                  </button>
                </div>

                {/* Progress Bar */}
                <div className="flex items-center mt-4 space-x-2">
                  <div className={`flex-1 h-2 rounded-full ${paymentStep >= 1 ? 'bg-white' : 'bg-white/30'}`}></div>
                  <div className={`flex-1 h-2 rounded-full ${paymentStep >= 2 ? 'bg-white' : 'bg-white/30'}`}></div>
                  <div className={`flex-1 h-2 rounded-full ${paymentStep >= 3 ? 'bg-white' : 'bg-white/30'}`}></div>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6">
                {/* Step 1: Delivery Details */}
                {paymentStep === 1 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    {/* Product Summary */}
                    <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-4 mb-6">
                      <div className="flex items-center space-x-4">
                        <img
                          src={selectedProduct.image}
                          alt={selectedProduct.name}
                          className="w-20 h-20 object-cover rounded-xl"
                        />
                        <div className="flex-1">
                          <h3 className="font-bold text-gray-800">{selectedProduct.name}</h3>
                          <p className="text-sm text-gray-600">Quantity: {quantity}</p>
                          <p className="text-lg font-bold text-teal-600 mt-1">
                            ₹{selectedProduct.discountPrice * quantity}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Delivery Address */}
                    <div className="mb-6">
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        <FaMapMarkerAlt className="inline mr-2 text-teal-500" />
                        Delivery Address *
                      </label>
                      <textarea
                        value={deliveryAddress}
                        onChange={(e) => {
                          setDeliveryAddress(e.target.value);
                          setFormErrors(prev => ({ ...prev, address: '' }));
                        }}
                        placeholder="Enter your complete delivery address including house/flat number, street, area, city, state, and pincode"
                        rows="4"
                        className={`w-full p-4 border-2 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all ${formErrors.address ? 'border-red-300' : 'border-gray-200'
                          }`}
                      />
                      {formErrors.address && (
                        <p className="mt-2 text-sm text-red-600 flex items-center">
                          <FaExclamationTriangle className="mr-2" />
                          {formErrors.address}
                        </p>
                      )}
                    </div>

                    {/* Phone Number */}
                    <div className="mb-6">
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        <FaPhone className="inline mr-2 text-teal-500" />
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        value={phoneNumber}
                        onChange={(e) => {
                          setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 10));
                          setFormErrors(prev => ({ ...prev, phone: '' }));
                        }}
                        placeholder="Enter 10-digit mobile number"
                        maxLength="10"
                        className={`w-full p-4 border-2 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all ${formErrors.phone ? 'border-red-300' : 'border-gray-200'
                          }`}
                      />
                      {formErrors.phone && (
                        <p className="mt-2 text-sm text-red-600 flex items-center">
                          <FaExclamationTriangle className="mr-2" />
                          {formErrors.phone}
                        </p>
                      )}
                    </div>

                    {/* Continue Button */}
                    <button
                      onClick={handleContinueToPayment}
                      className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 text-white py-4 rounded-xl font-bold text-lg hover:shadow-xl transition-all flex items-center justify-center space-x-2"
                    >
                      <span>Continue to Payment</span>
                      <FaArrowRight />
                    </button>
                  </motion.div>
                )}

                {/* Step 2: Payment */}
                {paymentStep === 2 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    {/* Order Summary */}
                    <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-2xl p-4 mb-6 border border-teal-200">
                      <h3 className="font-bold text-gray-800 mb-3">Order Summary</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Product Total:</span>
                          <span className="font-bold">₹{selectedProduct.discountPrice * quantity}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Delivery:</span>
                          <span className="font-bold text-green-600">FREE</span>
                        </div>
                        <div className="border-t border-teal-200 pt-2 mt-2 flex justify-between">
                          <span className="font-bold text-gray-800">Total Amount:</span>
                          <span className="font-bold text-xl text-teal-600">₹{selectedProduct.discountPrice * quantity}</span>
                        </div>
                      </div>
                    </div>

                    {/* Payment Method Selection */}
                    <div className="mb-6">
                      <label className="block text-sm font-bold text-gray-700 mb-3">
                        <FaCreditCard className="inline mr-2 text-teal-500" />
                        Select Payment Method *
                      </label>
                      <div className="grid grid-cols-2 gap-4">
                        {/* Google Pay */}
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => {
                            setSelectedPaymentMethod('GooglePay');
                            setFormErrors(prev => ({ ...prev, paymentMethod: '' }));
                          }}
                          className={`p-4 rounded-xl border-2 transition-all ${selectedPaymentMethod === 'GooglePay'
                            ? 'border-teal-500 bg-teal-50'
                            : 'border-gray-200 hover:border-gray-300'
                            }`}
                        >
                          <div className="text-center">
                            <div className="text-4xl mb-2">💳</div>
                            <p className="font-bold text-gray-800">Google Pay</p>
                          </div>
                        </motion.button>

                        {/* PhonePe */}
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => {
                            setSelectedPaymentMethod('PhonePe');
                            setFormErrors(prev => ({ ...prev, paymentMethod: '' }));
                          }}
                          className={`p-4 rounded-xl border-2 transition-all ${selectedPaymentMethod === 'PhonePe'
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-gray-200 hover:border-gray-300'
                            }`}
                        >
                          <div className="text-center">
                            <div className="text-4xl mb-2">📱</div>
                            <p className="font-bold text-gray-800">PhonePe</p>
                          </div>
                        </motion.button>
                      </div>
                      {formErrors.paymentMethod && (
                        <p className="mt-2 text-sm text-red-600 flex items-center">
                          <FaExclamationTriangle className="mr-2" />
                          {formErrors.paymentMethod}
                        </p>
                      )}
                    </div>

                    {/* QR Code Display */}
                    {selectedPaymentMethod && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-6 bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-6 text-center"
                      >
                        <FaQrcode className="text-6xl text-gray-400 mx-auto mb-4" />
                        <h4 className="font-bold text-gray-800 mb-2">
                          Scan QR Code to Pay
                        </h4>
                        <p className="text-sm text-gray-600 mb-4">
                          Pay ₹{selectedProduct.discountPrice * quantity} using {selectedPaymentMethod}
                        </p>
                        <div className="bg-white p-4 rounded-xl inline-block">
                          <p className="text-xs text-gray-500 mb-2">
                            {selectedPaymentMethod === 'GooglePay' ? 'Google Pay QR Code' : 'PhonePe QR Code'}
                          </p>
                          {qrLoading ? (
                            <div className="w-48 h-48 bg-gray-200 rounded-lg flex items-center justify-center">
                              <FaSpinner className="text-3xl text-gray-400 animate-spin" />
                            </div>
                          ) : (
                            <div className="w-48 h-48 bg-gray-200 rounded-lg flex items-center justify-center overflow-hidden">
                              {(selectedPaymentMethod === 'GooglePay' && paymentQRCodes.googlePay) ||
                                (selectedPaymentMethod === 'PhonePe' && paymentQRCodes.phonePe) ? (
                                <img
                                  src={selectedPaymentMethod === 'GooglePay' ? paymentQRCodes.googlePay : paymentQRCodes.phonePe}
                                  alt={`${selectedPaymentMethod} QR Code`}
                                  className="w-full h-full object-contain"
                                />
                              ) : (
                                <div className="text-center p-4">
                                  <FaQrcode className="text-4xl text-gray-400 mx-auto mb-2" />
                                  <p className="text-gray-500 text-xs">QR Code not available</p>
                                  <p className="text-gray-400 text-xs mt-1">Please contact admin</p>
                                </div>
                              )}
                            </div>
                          )}
                          <p className="text-xs text-gray-500 mt-2">
                            Scan to complete payment
                          </p>
                        </div>
                      </motion.div>
                    )}

                    {/* Payment Screenshot Upload */}
                    <div className="mb-6">
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        <FaUpload className="inline mr-2 text-teal-500" />
                        Upload Payment Screenshot *
                      </label>
                      <div className="relative">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handlePaymentScreenshotUpload}
                          className="hidden"
                          id="screenshot-upload"
                        />
                        <label
                          htmlFor="screenshot-upload"
                          className={`block w-full p-4 border-2 border-dashed rounded-xl cursor-pointer hover:border-teal-500 transition-all ${formErrors.screenshot ? 'border-red-300' : 'border-gray-300'
                            }`}
                        >
                          {paymentScreenshotPreview ? (
                            <div className="text-center">
                              <img
                                src={paymentScreenshotPreview}
                                alt="Payment Screenshot"
                                className="max-h-40 mx-auto rounded-lg mb-2"
                              />
                              <p className="text-sm text-green-600 font-medium">
                                <FaCheckCircle className="inline mr-2" />
                                Screenshot uploaded successfully
                              </p>
                            </div>
                          ) : (
                            <div className="text-center text-gray-500">
                              <FaUpload className="text-3xl mx-auto mb-2" />
                              <p className="text-sm">Click to upload payment screenshot</p>
                            </div>
                          )}
                        </label>
                      </div>
                      {formErrors.screenshot && (
                        <p className="mt-2 text-sm text-red-600 flex items-center">
                          <FaExclamationTriangle className="mr-2" />
                          {formErrors.screenshot}
                        </p>
                      )}
                    </div>

                    {/* UPI ID */}
                    <div className="mb-6">
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        <FaUser className="inline mr-2 text-teal-500" />
                        Your UPI ID *
                      </label>
                      <input
                        type="text"
                        value={upiId}
                        onChange={(e) => {
                          setUpiId(e.target.value);
                          setFormErrors(prev => ({ ...prev, upiId: '' }));
                        }}
                        placeholder="yourname@paytm or yourname@phonepe"
                        className={`w-full p-4 border-2 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all ${formErrors.upiId ? 'border-red-300' : 'border-gray-200'
                          }`}
                      />
                      {formErrors.upiId && (
                        <p className="mt-2 text-sm text-red-600 flex items-center">
                          <FaExclamationTriangle className="mr-2" />
                          {formErrors.upiId}
                        </p>
                      )}
                    </div>

                    {/* Error Message */}
                    {formErrors.submit && (
                      <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
                        <FaExclamationTriangle className="inline mr-2" />
                        {formErrors.submit}
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-4">
                      <button
                        onClick={handleBackToDelivery}
                        className="flex-1 bg-gray-100 text-gray-700 py-4 rounded-xl font-bold hover:bg-gray-200 transition-all flex items-center justify-center space-x-2"
                      >
                        <FaChevronLeft />
                        <span>Back</span>
                      </button>
                      <button
                        onClick={handleSubmitOrder}
                        disabled={isProcessingOrder}
                        className={`flex-1 bg-gradient-to-r from-teal-500 to-emerald-500 text-white py-4 rounded-xl font-bold hover:shadow-xl transition-all flex items-center justify-center space-x-2 ${isProcessingOrder ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                      >
                        {isProcessingOrder ? (
                          <>
                            <FaSpinner className="animate-spin" />
                            <span>Processing...</span>
                          </>
                        ) : (
                          <>
                            <FaCheck />
                            <span>Confirm Order</span>
                          </>
                        )}
                      </button>
                    </div>
                  </motion.div>
                )}

                {/* Step 3: Confirmation */}
                {paymentStep === 3 && orderSuccess && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-8"
                  >
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", delay: 0.2 }}
                      className="bg-gradient-to-r from-green-500 to-emerald-500 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6"
                    >
                      <FaCheckCircle className="text-5xl text-white" />
                    </motion.div>

                    <h3 className="text-3xl font-bold text-gray-800 mb-2">
                      Order Placed Successfully!
                    </h3>
                    <p className="text-gray-600 mb-6">
                      Your order has been received and is being processed
                    </p>

                    <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-2xl p-6 mb-6 border border-teal-200">
                      <p className="text-sm text-gray-600 mb-2">Order ID</p>
                      <p className="text-2xl font-bold text-teal-600 mb-4">{orderId}</p>

                      <div className="text-left space-y-3 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Product:</span>
                          <span className="font-medium">{selectedProduct.name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Quantity:</span>
                          <span className="font-medium">{quantity}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total Amount:</span>
                          <span className="font-bold text-teal-600">₹{selectedProduct.discountPrice * quantity}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Payment Method:</span>
                          <span className="font-medium">{selectedPaymentMethod}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Status:</span>
                          <span className="font-medium text-orange-600">Pending Verification</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                      <p className="text-sm text-blue-800">
                        <FaExclamationTriangle className="inline mr-2" />
                        Your payment will be verified shortly. You will receive a confirmation once verified.
                      </p>
                    </div>

                    <button
                      onClick={handleClosePaymentModal}
                      className="bg-gradient-to-r from-teal-500 to-emerald-500 text-white px-8 py-4 rounded-xl font-bold hover:shadow-xl transition-all"
                    >
                      Continue Shopping
                    </button>
                  </motion.div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toast Notification */}
      <AnimatePresence>
        {cart.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-6 right-6 bg-gradient-to-r from-teal-500 to-emerald-500 text-white px-6 py-3 rounded-xl shadow-xl z-50"
          >
            <div className="flex items-center space-x-3">
              <FaShoppingCart className="text-xl" />
              <div>
                <div className="font-bold">Added to Cart!</div>
                <div className="text-sm opacity-90">
                  {totalCartItems} item{totalCartItems !== 1 ? "s" : ""} in cart
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer */}
      <motion.footer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="mt-16 border-t border-gray-200 bg-white"
      ></motion.footer>
    </div>
  );
};

export default Product;
